import type { Placement } from '@vielzeug/orbit';

import {
  bind,
  createStableId,
  define,
  getHost,
  html,
  onCleanup,
  onMounted,
  prop,
  useEmit,
  useSlots,
} from '@vielzeug/ore';
import { computed, signal } from '@vielzeug/ripple';
import { type OverlayOpenChangeDetail, type OverlayOpenReason, parseStringTriggers } from '../../core';
import { disablableBundle, sizableBundle } from '../../shared';
import { forcedColorsMixin } from '../../styles';
import type { ComponentSize } from '../../types';
import { useFloatingTrigger } from '../shared/use-floating-trigger';
import styles from './tooltip.css?inline';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
type TooltipTrigger = 'click' | 'focus' | 'hover';

const LEFT_GAP_COMPENSATION = 4;
const DEFAULT_TOOLTIP_TRIGGERS: TooltipTrigger[] = ['hover', 'focus'];
const VALID_TOOLTIP_TRIGGERS = new Set<TooltipTrigger>(['click', 'focus', 'hover']);

const parseDelayMs = (value: string | null): number => {
  if (value == null || value.trim() === '') return 0;

  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const parseOptionalBool = (value: string | null): boolean | undefined =>
  value == null ? undefined : value === '' || value === 'true';

const normalizeTriggers = (value: string | null | undefined): TooltipTrigger[] =>
  parseStringTriggers(value, VALID_TOOLTIP_TRIGGERS, DEFAULT_TOOLTIP_TRIGGERS);

/** Tooltip component properties */
export type OreTooltipEvents = {
  'open-change': OverlayOpenChangeDetail;
};

export type OreTooltipProps = {
  /** Hide delay in ms */
  'close-delay'?: number;
  /** Tooltip text content */
  content?: string;
  /** Initial uncontrolled open state. Ignored when `open` is set. */
  'default-open'?: boolean;
  /** Show delay in ms */
  delay?: number;
  /** Disable the tooltip */
  disabled?: boolean;
  /** Controlled open state */
  open?: boolean;
  /** Preferred placement relative to trigger */
  placement?: TooltipPlacement;
  /** Tooltip size */
  size?: ComponentSize;
  /** Which trigger(s) show/hide the tooltip */
  trigger?: string;
  /** Visual variant */
  variant?: 'dark' | 'light';
};

/**
 * A lightweight tooltip shown on hover/focus/click relative to the slotted trigger.
 *
 * @element ore-tooltip
 *
 * @attr {string} content - Tooltip text content
 * @attr {string} placement - 'top' | 'bottom' | 'left' | 'right' (default: 'top')
 * @attr {string} trigger - 'hover' | 'focus' | 'click' or comma-separated combination
 * @attr {number} delay - Show delay in milliseconds (default: 0)
 * @attr {boolean} open - Controlled open state
 * @attr {boolean} default-open - Initial uncontrolled open state
 * @attr {string} size - Size: 'sm' | 'md' | 'lg'
 * @attr {string} variant - 'dark' (default) | 'light'
 * @attr {boolean} disabled - Disable the tooltip
 *
 * @slot - Trigger element that the tooltip is anchored to
 * @slot content - Complex tooltip content (overrides the `content` attribute)
 * @fires open-change - Fired when the tooltip state changes. detail: { open, reason }
 *
 * @cssprop --tooltip-max-width - Max width of the tooltip bubble
 *
 * @part tooltip - Tooltip container.
 * @example
 * ```html
 * <!-- Simple text tooltip -->
 * <ore-tooltip content="Copy to clipboard">
 *   <button>Copy</button>
 * </ore-tooltip>
 *
 * <!-- Placement and delay -->
 * <ore-tooltip content="Save your work" placement="right" delay="300">
 *   <ore-icon name="save"></ore-icon>
 * </ore-tooltip>
 *
 * <!-- Hover + focus trigger -->
 * <ore-tooltip content="Required field" trigger="hover,focus" placement="top">
 *   <ore-input label="Email" type="email"></ore-input>
 * </ore-tooltip>
 *
 * <!-- Rich content slot -->
 * <ore-tooltip>
 *   <ore-button>Help</ore-button>
 *   <div slot="content">
 *     <strong>Keyboard shortcuts</strong>
 *     <p>Press Ctrl+S to save.</p>
 *   </div>
 * </ore-tooltip>
 * ```
 */
export const TOOLTIP_TAG = 'ore-tooltip' as const;
define<OreTooltipProps>(TOOLTIP_TAG, {
  props: {
    ...sizableBundle,
    ...disablableBundle,
    'close-delay': { default: 0, parse: parseDelayMs },
    content: prop.string(),
    'default-open': prop.bool(false),
    delay: { default: 0, parse: parseDelayMs },
    open: { default: undefined as boolean | undefined, parse: parseOptionalBool },
    placement: prop.oneOf(['top', 'bottom', 'left', 'right'] as const, 'top'),
    trigger: prop.string('hover,focus'),
    variant: prop.string<'dark' | 'light'>(),
  },
  setup(props) {
    const el = getHost();
    const emit = useEmit<OreTooltipEvents>();
    const slots = useSlots();

    const shadowRoot = el.shadowRoot;
    const isDisabled = computed(() => Boolean(props.disabled.value));
    const isControlled = computed(() => props.open.value !== undefined);
    const activePlacement = signal<TooltipPlacement>('top');
    const tooltipId = createStableId('tooltip');
    const triggers = computed<TooltipTrigger[]>(() => normalizeTriggers(props.trigger.value));
    let tooltipEl: HTMLElement | null = null;
    let showTimer: ReturnType<typeof setTimeout> | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const clearShowTimer = (): void => {
      if (!showTimer) return;

      clearTimeout(showTimer);
      showTimer = null;
    };
    const clearHideTimer = (): void => {
      if (!hideTimer) return;

      clearTimeout(hideTimer);
      hideTimer = null;
    };

    const floating = useFloatingTrigger({
      bindTriggerAria: (triggerEl) => bind({ aria: { describedby: () => tooltipId } }, { target: triggerEl }),
      defaultOpen: props['default-open'],
      disabled: isDisabled,
      getPanel: () => tooltipEl,
      offset: 8,
      onCleanup,
      onClose: (reason) => emit('open-change', { open: false, reason }),
      onOpen: (reason) => emit('open-change', { open: true, reason }),
      onPlacementChange: (p) => {
        const side = p.split('-')[0] as TooltipPlacement;

        activePlacement.value = side;

        if (side === 'left' && tooltipEl) {
          tooltipEl.style.left = `${parseFloat(tooltipEl.style.left) - LEFT_GAP_COMPENSATION}px`;
        }
      },
      openProp: props.open as typeof props.open & { value: boolean | undefined },
      placement: computed(() => props.placement.value as Placement),
      slot: () => shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])') ?? null,
      slotElements: slots.elements(),
      triggers: computed(() => [] as TooltipTrigger[]),
    });

    function show(reason: OverlayOpenReason = 'hover'): void {
      if (isControlled.value) return;

      if (isDisabled.value || (!props.content.value && !slots.has('content').value)) return;

      clearHideTimer();
      clearShowTimer();

      const delay = Number(props.delay.value) || 0;

      if (delay > 0) {
        showTimer = setTimeout(() => {
          showTimer = null;
          floating.open(reason);
          floating.updatePosition();
        }, delay);
      } else {
        floating.open(reason);
        floating.updatePosition();
      }
    }

    function hide(): void {
      if (isControlled.value) return;

      clearShowTimer();

      const closeDelay = Number(props['close-delay'].value) || 0;

      if (closeDelay > 0) {
        clearHideTimer();
        hideTimer = setTimeout(() => {
          hideTimer = null;
          floating.close('trigger');
        }, closeDelay);
      } else {
        floating.close('trigger');
      }
    }

    onMounted(() => {
      // Manually bind trigger events so we can wrap show/hide with delay timers.
      // useFloatingTrigger handles ARIA, positioning, controlled mode, and keyboard dismiss.
      const triggerSlot = shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])');
      let eventCleanups: Array<() => void> = [];

      const addEvent = (target: EventTarget, event: string, handler: EventListener): void => {
        target.addEventListener(event, handler);
        eventCleanups.push(() => target.removeEventListener(event, handler));
      };

      const bindTriggerEvents = (): void => {
        for (const cleanup of eventCleanups) cleanup();
        eventCleanups = [];

        const triggerEl = triggerSlot?.assignedElements({ flatten: true })[0] as HTMLElement | undefined;

        if (!triggerEl) return;

        const t = triggers.value;

        if (t.includes('hover')) {
          addEvent(triggerEl, 'pointerenter', () => show('hover'));
          addEvent(triggerEl, 'pointerleave', hide);
        }

        if (t.includes('focus')) {
          addEvent(triggerEl, 'focusin', () => show('focus'));
          addEvent(triggerEl, 'focusout', hide);
        }

        if (t.includes('click')) {
          addEvent(triggerEl, 'click', () => (floating.visible.value ? hide() : show('click')));
        }
      };

      triggerSlot?.addEventListener('slotchange', bindTriggerEvents);
      bindTriggerEvents();

      const destroyFloating = floating.mount();

      return () => {
        triggerSlot?.removeEventListener('slotchange', bindTriggerEvents);
        for (const cleanup of eventCleanups) cleanup();
        clearShowTimer();
        clearHideTimer();
        destroyFloating();
      };
    });

    return html`
      <slot></slot>
      <div
        class="tooltip"
        part="tooltip"
        id="${tooltipId}"
        role="tooltip"
        popover="manual"
        ref=${(ref: HTMLElement) => {
          tooltipEl = ref;
        }}
        data-placement="${activePlacement}"
        aria-hidden="${() => String(!floating.visible.value)}">
        <slot name="content"><span class="tooltip-text">${props.content}</span></slot>
      </div>
    `;
  },
  styles: [forcedColorsMixin, styles],
});
